
import { mapActions, mapGetters } from 'vuex';
import CustomModal from '~/components/ui/molecule/CustomModal.vue';
import CreatePortfolioAssetOperationModal from '@/components/ui/modal/CreatePortfolioOperationModal/CreatePortfolioAssetOperationModal';
import CreatePortfolioCashOperationModal from '@/components/ui/modal/CreatePortfolioOperationModal/CreatePortfolioCashOperationModal';
import CreatePortfolioOperationModalHeader from '@/components/ui/modal/CreatePortfolioOperationModal/CreatePortfolioOperationModalHeader';
import CreatePortfolioOperationModalFooter from '@/components/ui/modal/CreatePortfolioOperationModal/CreatePortfolioOperationModalFooter';
import { isMobile } from '~/utils/screenSizes';
import { showTransactionPendingToast } from '~/utils/toast-notifications';
import { PortfolioOrderTypes } from '@/utils/constants/portfolioConstants';

/**
 * The main portfolio operation modal.
 *
 * This modal currently does NOT validate the symbol/ticker type it's provided for transaction support.
 * It may only be used with transactable symbols (see domain/transactions-behavior.ts).
 * Every caller that can open this modal with a preselected symbol MUST ensure it's a transactable symbol.

 * Callers that let the user search for a symbol from within the modal (at the time of writing, CreatePortfolioAssetOperationModal.vue, UIOrderBookModal.vue, might be outdated) must exclude unsupported symbol types from search (at the time of writing, via  integrations/api/transactable-symbol-search.ts).
 * (This may be outdated at the time of reading: for example, the UICompanyProfile must check areTransactionsSupported because it passes the symbol indirectly to this modal through the PF selector modal)
 */
export default {
  name: 'CreatePortfolioOperationModalWrapper',
  components: {
    CreatePortfolioCashOperationModal,
    CreatePortfolioAssetOperationModal,
    CreatePortfolioOperationModalHeader,
    CreatePortfolioOperationModalFooter,
    CustomModal,
  },
  data() {
    return {
      formDate: null,
      maxDate: this.$dayjs().subtract(2, 'day').format('YYYY-MM-DD'),
      daysToSubstract: 2,
      shouldShowTickerSearch: false,
      shouldShowPortfolioSearch: false,
      valueLoading: true,
      cashAvailableAtDate: {},
      availablePortfolioCurrencies: [],
      formButtonState: 'disabled',
      formButtonTooltip: '',
      totalAmount: 0,
      currentChosenCurrency: '',
      transactionError: false,
      isMultipleTransactions: false,
      timeoutId: null,
      preventReload: false,
      preventClose: false,
      preventCallback: false,
      selectedOrderType: null,
      isOrderValid: false,
    };
  },
  async fetch() {
    if (process.client) {
      // TODO - TO REMOVE - Portfolio selection step done in the portfolio selection modal
      if (!this.createPortfolioOperationModal.portfolio) {
        await this.getMyPortfolios();

        const currentSymbolName =
          this.createPortfolioOperationModal?.symbol?.name;

        const eligiblePortfolios =
          this.getMyPortfoliosRes.data.portfolios.filter(
            (p) =>
              !p.leagueId ||
              !p.league?.hasStockWhitelist ||
              p.leagueWhitelistedStocks?.some(
                (s) => s?.toUpperCase() === currentSymbolName
              )
          );

        const portfolio = await this.getPortfolioById(eligiblePortfolios[0].id);

        this.setCreateOperationModalModalPortfolio(portfolio);
      } else {
        // Some places open this modal with a lightweight portfolio (e.g. a list
        // item), so we fetch the full portfolio by id instead of relying on the
        // shared getter.
        const portfolio = await this.getPortfolioById(
          this.createPortfolioOperationModal.portfolio.id
        );

        this.setCreateOperationModalModalPortfolio(portfolio);
      }

      this.formDate = this.maxDate;
      await this.handleDateChange(this.formDate, null);
    }
  },
  computed: {
    ...mapGetters('portfolio', [
      'getMyPortfoliosRes',
      'createOperationRes',
      'createPortfolioOrderRes',
      'createCashOperationRes',
      'addPortfolioItemRes',
      'validatePortfolioOperationRes',
    ]),
    ...mapGetters('modals', ['createPortfolioOperationModal']),
    // Single source of truth for the portfolio the modal operates on. It is
    // provided when the modal is opened (or chosen within the modal) and never
    // read from the shared getPortfolioById getter, which can point at a
    // different portfolio viewed elsewhere in the app.
    activePortfolio() {
      return this.createPortfolioOperationModal.portfolio;
    },
    portfolioId() {
      return this.activePortfolio?.id ?? null;
    },
    isCrypto() {
      return this.createPortfolioOperationModal.portfolioType == 'Crypto';
    },
    tickerToAdd() {
      this.setCurrencyByStock(
        this.createPortfolioOperationModal.tickerValue?.currency
      );
      return this.createPortfolioOperationModal.tickerValue == ''
        ? null
        : this.createPortfolioOperationModal.tickerValue;
    },
    isMobileSize() {
      return isMobile();
    },
    shouldCheckForRankedRules() {
      return this.activePortfolio?.isPublic;
    },
    lineRuleLimit() {
      return (
        this.activePortfolio?.league?.minLinesToEnterRankings ??
        this.activePortfolio?.privacyConditions?.minLinesToEnterRankings
      );
    },
    orderTypes() {
      return PortfolioOrderTypes;
    },
    orderValidityMessage() {
      // Note - if the validation condition changes, remember to edit this message building step
      if (this.isOrderValid === null) {
        return null;
      }

      if (this.isOrderValid) {
        return '';
      }

      const operationType =
        this.createPortfolioOperationModal.operationType?.toLowerCase();

      if (!['buy', 'sell'].includes(operationType)) {
        return null;
      }

      return this.$t(
        `market_order_not_authorized_pending_opposite_order.${operationType}`
      );
    },
  },
  watch: {
    'createOperationRes.loading'(value) {
      this.formButtonState = value ? 'loading' : 'disabled';
    },
    'createPortfolioOrderRes.loading'(value) {
      this.formButtonState = value ? 'loading' : 'disabled';
    },
  },
  async mounted() {
    // we close the modal with swipe and back button
    window.history.pushState({ transactionModalOpen: true }, '');
    window.addEventListener('popstate', this.handleClose);

    // Show info toast if operation is already in progress
    if (
      this.createOperationRes?.loading ||
      this.addPortfolioItemRes?.loading ||
      this.validatePortfolioOperationRes?.loading
    ) {
      showTransactionPendingToast(this.$t('transaction_processing'));
    }
  },
  beforeDestroy() {
    window.removeEventListener('popstate', this.handleClose);
    // If the modal closed without a back press/swipe, remove the history entry we added.
    if (window.history.state?.transactionModalOpen) {
      window.history.back();
    }
  },
  created() {
    this.getMaxDate();
    this.shouldShowTickerSearch = !this.createPortfolioOperationModal.symbol;
    this.shouldShowPortfolioSearch =
      !this.createPortfolioOperationModal.portfolio;
  },
  methods: {
    ...mapActions('portfolio', [
      'getMyPortfolios',
      'createPortfolioOrder',
      'addPortfolioItem',
      'getPortfolioById',
      'createOperation',
      'createCashOperation',
      'validatePortfolioItemAddition',
      'validatePortfolioOperation',
    ]),
    ...mapActions('modals', [
      'showCreatePortfolioOperationModal',
      'switchCreatePortfolioOperation',
      'setOperationSymbol',
      'getTickerStockValue',
      'getCryptoTickerStockValue',
      'setCreateOperationModalModalPortfolio',
      'removeSymbolFromPortfolioOperation',
      'resetSubmitOperationState',
      'setNoDataAtDateError',
      'submitPortfolioOperationForm',
      'togglePortfolioOperationConfirmationComponent',
      'updatePortfolioOperationLineRuleFlag',
      'toggleUnrankedPortfolioConfirmationComponent',
    ]),
    ...mapActions('modals', ['showPortfolioAssetOperationModalFooter']),
    resetTransactionSubmitFlags() {
      this.preventReload = false;
      this.preventClose = false;
      this.preventCallback = false;
      this.transactionError = false;
    },
    startTransactionProcessingTimeout(shouldStayOpen, callback) {
      this.timeoutId = setTimeout(() => {
        showTransactionPendingToast(this.$t('transaction_processing'));

        if (!shouldStayOpen) {
          this.handleClose();
          return;
        }

        this.setCreateOperationModalModalPortfolio(this.activePortfolio);
        this.getCashAvailable();
        callback(true);
      }, 5000);
    },
    normalizeSubmitData(data) {
      const normalizedData = {
        ...data,
      };

      if (this.createPortfolioOperationModal.operationType === 'Sell') {
        normalizedData.chosenCurrency =
          this.createPortfolioOperationModal.tickerValue?.currency;
      }

      return normalizedData;
    },
    buildItemPayload(data, portfolioId, isCashTransaction) {
      return {
        portfolioId,
        symbolId: this.createPortfolioOperationModal.symbol.symbolId,
        companyProfileId: this.createPortfolioOperationModal.symbol.companyId,
        companyName: this.createPortfolioOperationModal.symbol.companyName,
        companyUri: this.createPortfolioOperationModal.symbol.avatarUri,
        symbolName: this.createPortfolioOperationModal.symbol.name,
        operationType: this.createPortfolioOperationModal.operationType,
        changePercent: this.createPortfolioOperationModal.symbol.changePercent,
        cryptocurrencyId: this.isCrypto
          ? this.createPortfolioOperationModal.symbol.companyId
          : null,
        ItemType: this.isCrypto ? 'Cryptocurrency' : 'Stocks',
        itemPrice: data.price,
        itemQuantity: data.quantity,
        date: data.date,
        isCashTransaction,
        currency: this.createPortfolioOperationModal.tickerValue.currency,
        chosenCurrency: data.chosenCurrency,
      };
    },
    buildOperationPayload(data, portfolioId, isCashTransaction) {
      const symbol = this.createPortfolioOperationModal.symbol;

      return {
        companyId: symbol.companyId,
        companyName: symbol.companyName ?? symbol.company,
        companyUri: symbol.avatarUri ?? symbol.companyAvatarUri,
        symbolName: symbol.name ?? symbol.symbol,
        changePercent: this.createPortfolioOperationModal.symbol.changePercent,
        portfolioId,
        currency: this.createPortfolioOperationModal.tickerValue.currency,
        chosenCurrency: data.chosenCurrency,
        isCashTransaction,
        lineRuleLimit: this.lineRuleLimit,
        ...data,
      };
    },
    buildMarketOrderPayload(data, portfolioId) {
      return {
        operationType: data.operationType,
        operationCurrency: data.chosenCurrency,
        sellStockQuantity: data.quantity,
        buyCashAmount: data.total,
        limitPrice: null,
        orderType: this.selectedOrderType,
        portfolioId,
        symbol:
          this.createPortfolioOperationModal.operationType === 'Buy'
            ? this.createPortfolioOperationModal.symbol.name
            : this.createPortfolioOperationModal.symbol.symbol,
        price: data.price,
        tickerCurrency: this.createPortfolioOperationModal.tickerValue.currency,
        changePercent:
          this.createPortfolioOperationModal.tickerValue.changePercent,
        lineRuleLimit: this.lineRuleLimit,
      };
    },
    buildLimitedOrderPayload(data, portfolioId) {
      return {
        operationType: data.operationType,
        operationCurrency: data.chosenCurrency,
        sellStockQuantity: data.quantity,
        buyCashAmount: data.total,
        limitPrice: data.orderLimitPrice,
        orderType: data.orderType,
        portfolioId,
        symbol:
          this.createPortfolioOperationModal.operationType === 'Buy'
            ? this.createPortfolioOperationModal.symbol.name
            : this.createPortfolioOperationModal.symbol.symbol,
        price: data.price,
        tickerCurrency: this.createPortfolioOperationModal.tickerValue.currency,
        changePercent:
          this.createPortfolioOperationModal.tickerValue.changePercent,
        lineRuleLimit: this.lineRuleLimit,
      };
    },
    async submitByOrderType({
      normalizedData,
      canMaintainRank,
      shouldStayOpen,
      callback,
      item,
    }) {
      if (this.selectedOrderType === this.orderTypes.MARKET_PRICE) {
        await this.handleMarketOrderSubmit({
          normalizedData,
          canMaintainRank,
          shouldStayOpen,
          callback,
          item,
        });
        return;
      } else if (this.selectedOrderType === this.orderTypes.LIMIT_PRICE) {
        await this.handleLimitedOrderSubmit({
          normalizedData,
          canMaintainRank,
          shouldStayOpen,
          callback,
          item,
        });
        return;
      }

      await this.handlePastOperationSubmit({
        normalizedData,
        canMaintainRank,
        shouldStayOpen,
        callback,
        item,
      });
    },
    async handleSubmit(data, shouldStayOpen, callback, isCashTransaction) {
      try {
        this.startTransactionProcessingTimeout(shouldStayOpen, callback);

        this.resetTransactionSubmitFlags();

        const normalizedData = this.normalizeSubmitData(data);
        const canMaintainRank = this.canMaintainRank(
          normalizedData.operationType,
          normalizedData.quantity
        );
        const portfolioId = this.portfolioId;

        this.itemPayload = this.buildItemPayload(
          normalizedData,
          portfolioId,
          isCashTransaction
        );
        this.operationPayload = this.buildOperationPayload(
          normalizedData,
          portfolioId,
          isCashTransaction
        );

        if (this.selectedOrderType === this.orderTypes.MARKET_PRICE) {
          this.marketOrderPayload = this.buildMarketOrderPayload(
            normalizedData,
            portfolioId
          );
        } else if (this.selectedOrderType === this.orderTypes.LIMIT_PRICE) {
          this.limitedOrderPayload = this.buildLimitedOrderPayload(
            normalizedData,
            portfolioId
          );
        }

        if (this.activePortfolio) {
          const item = this.activePortfolio.items.find(
            (e) =>
              e.symbolId === this.createPortfolioOperationModal.symbol.symbolId
          );

          await this.submitByOrderType({
            normalizedData,
            canMaintainRank,
            shouldStayOpen,
            callback,
            item,
          });
          this.resetSubmitOperationState();

          // setting transaction error message
          if (
            this.addPortfolioItemRes?.error ||
            this.createOperationRes?.error ||
            this.validatePortfolioOperationRes?.error
          ) {
            this.transactionError = true;
          }
          if (this.transactionError) {
            return;
          } else {
            if (shouldStayOpen && !this.preventCallback) {
              if (!this.preventReload) {
                const updatedPortfolio = await this.getPortfolioById(
                  portfolioId
                );
                this.setCreateOperationModalModalPortfolio(updatedPortfolio);
              }
              this.getCashAvailable();
              callback(true);
            } else {
              if (!this.preventClose) {
                this.handleClose();
              }
            }
          }
        }
      } finally {
        clearTimeout(this.timeoutId);
      }
    },
    async handleMarketOrderSubmit({
      normalizedData,
      canMaintainRank,
      shouldStayOpen,
      callback,
    }) {
      if (
        normalizedData.operationType === 'Sell' &&
        !canMaintainRank &&
        this.createPortfolioOperationModal.shouldCheckForLineRule
      ) {
        this.updatePortfolioOperationLineRuleFlag(false);
        this.preventReload = true;
        this.preventClose = true;
        this.preventCallback = true;

        callback(false);

        const payload = {
          show: true,
          marketOrderPayload: this.marketOrderPayload,
          isPublic: this.activePortfolio.isPublic,
          itemCountCondition: false,
          allocationCondition: true,
          transactionModalShouldStayOpen: shouldStayOpen,
          orderType: this.selectedOrderType,
          lineRuleLimit: this.marketOrderPayload.lineRuleLimit,
        };

        this.togglePortfolioOperationConfirmationComponent(payload);
        return;
      }

      await this.createPortfolioOrder(this.marketOrderPayload);
    },
    async handleLimitedOrderSubmit({
      normalizedData,
      canMaintainRank,
      shouldStayOpen,
      callback,
    }) {
      if (
        normalizedData.operationType === 'Sell' &&
        !canMaintainRank &&
        this.createPortfolioOperationModal.shouldCheckForLineRule
      ) {
        this.updatePortfolioOperationLineRuleFlag(false);
        this.preventReload = true;
        this.preventClose = true;
        this.preventCallback = true;

        callback(false);

        const payload = {
          show: true,
          limitedOrderPayload: this.limitedOrderPayload,
          isPublic: this.getPortfolioByIdRes.data.isPublic,
          itemCountCondition: false,
          allocationCondition: true,
          transactionModalShouldStayOpen: shouldStayOpen,
          orderType: this.selectedOrderType,
          lineRuleLimit: this.limitedOrderPayload.lineRuleLimit,
        };

        this.togglePortfolioOperationConfirmationComponent(payload);
        return;
      }

      await this.createPortfolioOrder(this.limitedOrderPayload);
    },
    async handlePastOperationSubmit({
      normalizedData,
      canMaintainRank,
      shouldStayOpen,
      callback,
      item,
    }) {
      if (item === undefined) {
        if (normalizedData.operationType === 'Sell') {
          window.$nuxt.$notify.warning(
            this.$t('operation_sell_without_shares')
          );
          return;
        }

        await this.addPortfolioItem(this.itemPayload);
        return;
      }

      if (
        !canMaintainRank &&
        this.createPortfolioOperationModal.shouldCheckForLineRule
      ) {
        this.updatePortfolioOperationLineRuleFlag(false);
        this.preventReload = true;
        this.preventClose = true;
        this.preventCallback = true;

        callback(false);

        const payload = {
          show: true,
          operationPayload: this.operationPayload,
          itemId: item.id,
          isPublic: this.activePortfolio.isPublic,
          itemCountCondition: false,
          allocationCondition: true,
          transactionModalShouldStayOpen: shouldStayOpen,
          lineRuleLimit: this.operationPayload.lineRuleLimit,
        };

        this.togglePortfolioOperationConfirmationComponent(payload);
        return;
      }

      await this.createOperation({
        itemId: item.id,
        ...this.operationPayload,
      });
    },
    async handleCashSubmit(data, shouldStayOpen, callback) {
      const portfolioId = this.portfolioId;
      const toSend = {
        portfolioId: portfolioId,
        ...data,
      };
      await this.createCashOperation(toSend);
      if (shouldStayOpen) {
        const updatedPortfolio = await this.getPortfolioById(portfolioId);
        this.setCreateOperationModalModalPortfolio(updatedPortfolio);
        this.getCashAvailable();
        callback(true);
      } else {
        this.handleClose();
      }
      this.resetSubmitOperationState();
    },
    handleCloseFromButton() {
      // Close via back navigation so the X button behaves like swipe/back.
      if (window.history.state?.transactionModalOpen) {
        window.history.back();
      } else {
        this.handleClose();
      }
    },
    handleClose() {
      this.setNoDataAtDateError(false);
      const payload = {
        symbol: null,
        portfolioType: null,
        show: false,
        currency: '',
      };
      this.showCreatePortfolioOperationModal(payload);
    },
    // @todo Figure out what name is? Name of portfolio, I assume. Document format.
    async handlePortfolioChange(name, callback) {
      if (!name) {
        return;
      }

      const portfolio = await this.getPortfolioById(
        this.getMyPortfoliosRes.data.portfolios.find((p) => p.name == name).id
      );
      this.setCreateOperationModalModalPortfolio(portfolio);

      this.getCashAvailable();

      // refreshing symbol price/conversions upon portfolio change
      await this.handleSymbolChange(
        this.createPortfolioOperationModal.symbol,
        callback
      );
    },
    /** TODO: Remove these callbacks with v-model... */
    async handleDateChange(date, callback) {
      if (
        !this.createPortfolioOperationModal.portfolio ||
        this.formdate == date
      ) {
        return;
      }
      this.formDate = date;
      this.getCashAvailable();
      if (!this.createPortfolioOperationModal.symbol) return;
      this.valueLoading = true;
      const symbolName = this.createPortfolioOperationModal.symbol.name
        ? this.createPortfolioOperationModal.symbol.name
        : this.createPortfolioOperationModal.symbol.symbol;
      const payload = {
        symbol: symbolName,
        date: date,
        portfolioId: this.createPortfolioOperationModal.portfolio.id,
        forceEodUpdate: true,
      };
      await (this.isCrypto
        ? this.getCryptoTickerStockValue(payload)
        : this.getTickerStockValue(payload));
      this.valueLoading = false;
      typeof callback === 'function' && callback(true);
    },
    /** TODO: Remove these callbacks with v-model... */
    async handleSymbolChange(symbol, callback) {
      this.setOperationSymbol(symbol);
      await this.handleDateChange(this.formDate, callback);
    },
    getCashAvailable() {
      const currency = this.createPortfolioOperationModal.portfolio.currency;
      const cashItems = this.activePortfolio.cashItems;
      const nextDay = this.$dayjs(this.formDate).add(1, 'day');
      this.cashAvailableAtDate = {};
      this.availablePortfolioCurrencies = [];

      cashItems.forEach((ci) => {
        const ciCurrencySymbol = this.$display.money.getCurrencySymbol(
          ci.currencyCode
        );
        const availableValueAtDate = ci.operations
          .filter((o) => this.$dayjs(o.operationDate).isBefore(nextDay))
          .reduce((sum, op) => sum + op.operationAmount, 0);

        this.cashAvailableAtDate[ci.currencyCode] = {
          // WIP - Shows some weird behavior on private PFs when cash is added and pending orders are present
          value: availableValueAtDate - (ci.pendingCharge ?? 0),
          symbol: ciCurrencySymbol,
          conversion:
            currency !== ci.currencyCode
              ? availableValueAtDate *
                ci.latestCashItemToPortfolioCurrencyConversionRate
              : availableValueAtDate,
        };
        this.availablePortfolioCurrencies.push(ci.currencyCode);
      });
    },
    getDefaultCurrency() {
      const defaultCurrency = 'EUR';
      if (this.createPortfolioOperationModal.currency !== '') {
        return this.createPortfolioOperationModal.currency;
      }
      return this.createPortfolioOperationModal.portfolio
        ? this.createPortfolioOperationModal.portfolio.currency
        : defaultCurrency;
    },
    getMaxDate() {
      const today = this.$dayjs().$d.getDay();
      // getDay() returns 0-6 (SUNDAY IS 0!!!)
      let toSubstract = 0;
      if (today === 6) {
        toSubstract = 1;
      } else if (today === 0) {
        toSubstract = 2;
      }
      this.daysToSubstract = toSubstract;
      this.maxDate = this.$dayjs()
        .subtract(toSubstract, 'day')
        .format('YYYY-MM-DD');
    },
    switchOperation(isCashTransaction) {
      const payload = {
        portfolio: this.activePortfolio,
        portfolioType: this.activePortfolio.portfolioType,
        show: true,
        isCashTransaction: isCashTransaction,
        shouldShowOperationSwitcher:
          this.createPortfolioOperationModal.shouldShowOperationSwitcher,
        shouldShowOperationButtons:
          this.createPortfolioOperationModal.shouldShowOperationButtons,
      };
      this.switchCreatePortfolioOperation(payload);
    },
    handleChangeOperationType(operation) {
      switch (operation.key) {
        case 'Stock':
          this.switchOperation(false);
          break;
        case 'Cash':
          this.switchOperation(true);
          break;
        default:
          return;
      }
    },
    handleButtonStateChange(buttonState) {
      this.formButtonState = buttonState;
    },
    handleButtonTooltipchange(buttonTooltip) {
      this.formButtonTooltip = buttonTooltip;
    },
    handleCurrencyChange(chosenCurrency) {
      if (this.createPortfolioOperationModal.operationType === 'Sell') {
        this.setCurrencyByStock(
          this.createPortfolioOperationModal.tickerValue?.currency
        );
      } else this.currentChosenCurrency = chosenCurrency;
    },
    handleAmountChange(amount) {
      this.totalAmount = amount;
    },
    handleRemoveSymbol() {
      this.removeSymbolFromPortfolioOperation();
      this.showPortfolioAssetOperationModalFooter(false);
      this.scrollSearchSymbolWrapperIntoView();
      this.setNoDataAtDateError(false);
    },
    scrollSearchSymbolWrapperIntoView() {
      setTimeout(() => {
        const wrapper = document.getElementById('searchSymbolWrapperId');
        if (wrapper) {
          wrapper.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 200);
    },
    handleTransactionTypeChange(operationType) {
      this.createPortfolioOperationModal.operationType = operationType;
    },
    setCurrencyByStock(currency) {
      if (this.createPortfolioOperationModal.operationType === 'Sell') {
        this.currentChosenCurrency =
          this.$display.money.getCurrencySymbol(currency);
      }
    },
    canMaintainRank(operationType, quantity) {
      const matchedSymbol = this.activePortfolio?.items?.find(
        (i) =>
          i.symbolId === this.createPortfolioOperationModal?.symbol?.symbolId
      );
      return !(
        this.activePortfolio &&
        this.shouldCheckForRankedRules &&
        this.activePortfolio.lineCount === this.lineRuleLimit &&
        operationType === 'Sell' &&
        matchedSymbol.quantity == quantity
      );
    },
    handleSetMultipleTransactions(multipleTransactions) {
      this.multipleTransactions = multipleTransactions;
    },
    handleSelectOrderType(type) {
      this.selectedOrderType = type;
    },
    handleShowTransactionTypesModal() {
      this.$emit('onShowTransactionTypesModal');
    },
    handleOrderValidityChange(isValid) {
      this.isOrderValid = isValid;
    },
  },
};
